import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _SAVE_FILTER_HANDLERS, _ACTION_HANDLERS;
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint-disable import/order */
/* eslint implicit-arrow-linebreak: 0 */
import produce from 'immer';
import { compose } from 'recompose';
import _head from 'lodash/head';
import _property from 'lodash/property';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _without from 'lodash/without';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _find from 'lodash/find';
import _values from 'lodash/values';
import _keyBy from 'lodash/keyBy';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _size from 'lodash/size';
import _noop from 'lodash/noop';
import _includes from 'lodash/includes';

// Services
import * as defaultFilterService from '@tekion/tekion-base/services/filterServices';

// Constants
import { EMPTY_OBJECT, EMPTY_ARRAY } from '@tekion/tekion-base/app.constants';
import getDataFromResponse from '@tekion/tekion-base/utils/getDataFromResponse';
import MODES from '../constants/filterDialog.modes';
import { ADD_NEW_FILTER, RESET_FILTER, APPLY_FILTERS } from '../molecules/filterFooter/constants/filterFooter.actionTypes';
import { HIDE_FILTER_POPOVER, SHOW_FILTER_POPOVER, REFRESH_FILTER_TYPES } from '../molecules/filterDialogSection/constants/filterDialogSection.actionTypes';
import FORM_ACTION_TYPES from '../../../FormBuilder/constants/actionTypes';
import { FILTER_GROUP_CHANGE, SAVE_AS_NEW_FILTER_GROUP, FILTER_GROUP_UPDATE, RENAME_FILTER_GROUP, DELETE_FILTER_GROUP } from '../molecules/filterHeader/molecules/filterHeaderView/filterHeaderView.actionTypes';
import { FILTER_GROUP_EDIT_CANCEL, FILTER_GROUP_NAME_CHANGE, FILTER_GROUP_SAVE } from '../molecules/filterHeader/molecules/filterHeaderEdit/filterHeaderEdit.actionTypes';
import { DEFAULT_FILTER_GROUP } from '../../constants/filterSection.filterGroups';
import { CLOSE_FILTER_DIALOG } from '../molecules/filterHeader/constants/filterHeader.actionTypes';

// Helpers
import makeFilterRowField from './filterDialog.makeField';
import { updateFieldOnTypeChange, updateValuesOnTypeChange } from './filterDialog.filterTypeChange';
import { getFilterId } from './filterDialog.filterType';
import { getInitialValues, getSectionRows, getFields } from './filterDialog.initialLoad';
import { getSelectedFilterGroup, getMergedFilters } from './filterDialog.general';
import addPresetToAdditionalInDateFilter from './filterDialog.dateFilter';
import { BINARY_OPERATORS } from '../constants/filterDialog.general';
import { getFilterGroupPayload, getFilterGroup, getFilterFromFormValues } from '../../helpers/filterSection.general';
import isFilterGroupModified from './filterDialog.modificationCheck';
import { fireApplyFilterGroupClickStreamEvent, fireDeleteFilterGroupClickStreamEvent, fireSaveNewFilterGroupClickStreamEvent, fireUpdateFilterGroupClickStreamEvent } from './filterDialog.clickStream';

// Readers
import filterGroupReader from '../../readers/FilterGroup';
import { DEFAULT_FILTER_BEHAVIOR } from '../../constants/filterSection.constants';
import style from '../filterDialog.module.scss';
var getFilterGroupName = function getFilterGroupName(group) {
  return group.name.toLowerCase();
};
var canAddNewFilterRows = function canAddNewFilterRows(sectionRows, filterTypes) {
  return _size(sectionRows) < _size(filterTypes);
};
var createFormSections = function createFormSections(sectionRows) {
  return [{
    className: style.sectionWrapper,
    rows: sectionRows
  }];
};
export var validateFilterGroup = function validateFilterGroup(filterGroupName, values, filterGroups, validateGroupName) {
  var errors = {};
  if (validateGroupName) {
    var filterGroupNames = _map(filterGroups, getFilterGroupName);
    if (!filterGroupName) {
      errors.filterGroupName = __('This field is mandatory');
    } else if (filterGroupNames.includes(filterGroupName.toLowerCase())) {
      errors.filterGroupName = __('Duplicate Name');
    }
  }
  return errors;
};
var isFilterGroupEdited = function isFilterGroupEdited(_ref) {
  var getState = _ref.getState,
    values = _ref.values,
    sections = _ref.sections,
    selectedFilterGroup = _ref.selectedFilterGroup;
  var _getState = getState(),
    filterGroups = _getState.filterGroups;
  var selectedGroup = getSelectedFilterGroup(selectedFilterGroup, filterGroups);
  var sectionRows = _get(sections, '0.rows');
  return isFilterGroupModified({
    values: values,
    selectedFilterGroup: selectedGroup
  }, sectionRows);
};
var getUpdatedSections = function getUpdatedSections(sections, newFilterId) {
  return produce(sections, function (draft) {
    var section = _head(draft);
    if (!section) {
      draft[0] = {
        rows: [newFilterId]
      };
    } else {
      section.rows.push(newFilterId);
    }
  });
};
var getDefaultFilterType = compose(getFilterId, _head);
var getDefaultOperator = compose(_property('id'), _head);
var getRenderOptions = _property('renderOptions');
var getValuesForField = function getValuesForField() {
  var newFilterField = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var _getRenderOptions = getRenderOptions(newFilterField),
    filterTypes = _getRenderOptions.filterTypes,
    operators = _getRenderOptions.operators;
  var defaultFilterType = getDefaultFilterType(filterTypes);
  var defaultOperator = getDefaultOperator(operators);
  return {
    type: defaultFilterType,
    operator: defaultOperator
  };
};
var handleAddNewFilter = function handleAddNewFilter(_ref2) {
  var getState = _ref2.getState,
    setState = _ref2.setState;
  var _getState2 = getState(),
    filterTypes = _getState2.filterTypes,
    values = _getState2.values,
    sections = _getState2.sections,
    selectedFilterGroup = _getState2.selectedFilterGroup,
    filterGroups = _getState2.filterGroups,
    defaultFilterTypes = _getState2.defaultFilterTypes,
    isReadOnly = _getState2.isReadOnly;
  var newFilterField = makeFilterRowField(defaultFilterTypes, filterTypes, values);
  var newSections = getUpdatedSections(sections, newFilterField.id);
  var newValues = _objectSpread(_objectSpread({}, values), {}, _defineProperty({}, newFilterField.id, getValuesForField(newFilterField)));
  var updatedFields = getFields(defaultFilterTypes, newValues, filterTypes, filterGroups, selectedFilterGroup, isReadOnly);
  var isModified = isFilterGroupEdited({
    getState: getState,
    sections: newSections,
    values: newValues,
    selectedFilterGroup: selectedFilterGroup
  });
  setState({
    fields: updatedFields,
    sections: newSections,
    values: newValues,
    isModified: isModified,
    showAddFilter: canAddNewFilterRows(_get(newSections, '0.rows'), filterTypes)
  });
};
var handlePopoverHide = function handlePopoverHide(_ref3) {
  var setState = _ref3.setState;
  setState({
    isPopoverVisible: false
  });
};
var getUpdatedFilterGroups = function getUpdatedFilterGroups(_ref4, _ref5) {
  var filterGroups = _ref4.filterGroups,
    defaultFilterValues = _ref4.defaultFilterValues,
    defaultFilterBehavior = _ref4.defaultFilterBehavior;
  var selectedFilterGroup = _ref5.selectedFilterGroup;
  if (defaultFilterBehavior !== DEFAULT_FILTER_BEHAVIOR.PERSIST_LOCAL || selectedFilterGroup !== DEFAULT_FILTER_GROUP) return filterGroups;
  return _map(filterGroups, function (filterGroup) {
    if (filterGroup.id === DEFAULT_FILTER_GROUP) return _objectSpread(_objectSpread({}, filterGroup), {}, {
      filters: defaultFilterValues
    });
    return filterGroup;
  });
};
var handleSelectedFilterGroupChange = function handleSelectedFilterGroupChange(_ref6) {
  var setState = _ref6.setState,
    getState = _ref6.getState,
    _ref6$params = _ref6.params,
    params = _ref6$params === void 0 ? EMPTY_OBJECT : _ref6$params;
  var _getState3 = getState(),
    filterTypes = _getState3.filterTypes,
    defaultFilterTypes = _getState3.defaultFilterTypes,
    onFilterApply = _getState3.onFilterApply,
    isReadOnly = _getState3.isReadOnly;
  var selectedFilterGroup = params.selectedFilterGroup;
  var updatedFilterGroups = getUpdatedFilterGroups(getState(), params);
  var valuesMap = getInitialValues(updatedFilterGroups, selectedFilterGroup);
  var fields = getFields(defaultFilterTypes, valuesMap, filterTypes, updatedFilterGroups, selectedFilterGroup, isReadOnly);
  var appliedFilterGroup = getSelectedFilterGroup(selectedFilterGroup, updatedFilterGroups);
  var filters = filterGroupReader.filters(appliedFilterGroup);
  var sectionRows = getSectionRows(valuesMap, filters);
  var newSections = createFormSections(sectionRows);
  setState({
    selectedFilterGroup: selectedFilterGroup,
    fields: fields,
    values: valuesMap,
    showAddFilter: canAddNewFilterRows(_get(newSections, '0.rows'), filterTypes),
    sections: newSections,
    nonRenamable: filterGroupReader.nonRenamable(appliedFilterGroup),
    nonDeletable: filterGroupReader.nonDeletable(appliedFilterGroup)
  });
  if (isReadOnly) {
    onFilterApply(selectedFilterGroup);
  }
};
var handlePopoverShow = function handlePopoverShow(_ref7) {
  var setState = _ref7.setState,
    getState = _ref7.getState;
  var _getState4 = getState(),
    filterGroups = _getState4.filterGroups,
    appliedFilterGroup = _getState4.appliedFilterGroup,
    filterTypes = _getState4.filterTypes,
    selectedFilters = _getState4.selectedFilters,
    defaultFilterTypes = _getState4.defaultFilterTypes,
    selectedFilterGroup = _getState4.selectedFilterGroup,
    isReadOnly = _getState4.isReadOnly;
  var initialValuesMap = getInitialValues(filterGroups, appliedFilterGroup || selectedFilterGroup, selectedFilters);
  var initialFields = getFields(defaultFilterTypes, initialValuesMap, filterTypes, filterGroups, appliedFilterGroup || selectedFilterGroup, isReadOnly);
  var computedSelectedFilterGroup = getSelectedFilterGroup(appliedFilterGroup || selectedFilterGroup, filterGroups);
  var filters = getMergedFilters(filterGroupReader.filters(computedSelectedFilterGroup), selectedFilters);
  var initialSectionRows = getSectionRows(initialValuesMap, filters);
  var initialSections = createFormSections(initialSectionRows);
  var isModified = isFilterGroupEdited({
    getState: getState,
    sections: initialSections,
    values: initialValuesMap,
    selectedFilterGroup: appliedFilterGroup || selectedFilterGroup
  });
  setState({
    isPopoverVisible: true,
    fields: initialFields,
    values: initialValuesMap,
    sections: initialSections,
    selectedFilterGroup: appliedFilterGroup || selectedFilterGroup,
    showAddFilter: canAddNewFilterRows(initialSectionRows, filterTypes),
    isModified: isModified,
    nonRenamable: filterGroupReader.nonRenamable(computedSelectedFilterGroup),
    nonDeletable: filterGroupReader.nonDeletable(computedSelectedFilterGroup)
  });
};
var handleTypeChangeInListItem = function handleTypeChangeInListItem(_ref8) {
  var getState = _ref8.getState,
    setState = _ref8.setState,
    _ref8$params = _ref8.params,
    params = _ref8$params === void 0 ? EMPTY_OBJECT : _ref8$params;
  var value = params.value,
    id = params.id;
  var _getState5 = getState(),
    filterTypes = _getState5.filterTypes,
    fields = _getState5.fields,
    currentValues = _getState5.values,
    sections = _getState5.sections,
    selectedFilterGroup = _getState5.selectedFilterGroup,
    filterGroups = _getState5.filterGroups,
    defaultFilterTypes = _getState5.defaultFilterTypes,
    isReadOnly = _getState5.isReadOnly;
  var filterTypeId = value.type;
  var updatedField = updateFieldOnTypeChange(filterTypes, filterTypeId, id, fields);
  var updatedValue = updateValuesOnTypeChange(value, updatedField);
  var newValues = _objectSpread(_objectSpread({}, currentValues), {}, _defineProperty({}, id, updatedValue));
  var updatedFields = getFields(defaultFilterTypes, newValues, filterTypes, filterGroups, selectedFilterGroup, isReadOnly);
  var isModified = isFilterGroupEdited({
    getState: getState,
    sections: sections,
    values: newValues,
    selectedFilterGroup: selectedFilterGroup
  });
  setState({
    values: newValues,
    fields: updatedFields,
    isModified: isModified
  });
};
var getUpdatedValuesBasedOnFilterType = function getUpdatedValuesBasedOnFilterType(allFilters, params, filterTypes) {
  var _params$value = params.value,
    filterDetails = _params$value === void 0 ? EMPTY_OBJECT : _params$value,
    filterId = params.id,
    fieldKey = params.fieldKey;
  var newOperator = filterDetails.operator;
  var previousOperator = _get(allFilters, "".concat(filterId, ".operator"));
  var filterDetailsWithUpdatedDateFilter = addPresetToAdditionalInDateFilter({
    filterDetails: filterDetails,
    allFilters: allFilters,
    filterId: filterId,
    filterTypes: filterTypes
  });
  var updatedFilters = _objectSpread(_objectSpread({}, allFilters), {}, _defineProperty({}, filterId, filterDetailsWithUpdatedDateFilter));
  var isBinaryOperatorFilter = _includes(BINARY_OPERATORS, newOperator) || _includes(BINARY_OPERATORS, previousOperator);
  if (fieldKey === 'operator' && isBinaryOperatorFilter) {
    var updatedFilterDetails = _objectSpread(_objectSpread({}, filterDetails), {}, {
      values: EMPTY_ARRAY
    });
    updatedFilters = _objectSpread(_objectSpread({}, allFilters), {}, _defineProperty({}, filterId, updatedFilterDetails));
  }
  return updatedFilters;
};
var handleChangeInListItem = function handleChangeInListItem(_ref9) {
  var getState = _ref9.getState,
    setState = _ref9.setState,
    _ref9$params = _ref9.params,
    params = _ref9$params === void 0 ? EMPTY_OBJECT : _ref9$params;
  var _getState6 = getState(),
    currentValues = _getState6.values,
    sections = _getState6.sections,
    selectedFilterGroup = _getState6.selectedFilterGroup,
    filterTypes = _getState6.filterTypes;
  var updatedFilterValues = getUpdatedValuesBasedOnFilterType(currentValues, params, filterTypes);
  var isModified = isFilterGroupEdited({
    getState: getState,
    sections: sections,
    values: updatedFilterValues,
    selectedFilterGroup: selectedFilterGroup
  });
  setState({
    values: updatedFilterValues,
    isModified: isModified
  });
};
var handleListItemChange = function handleListItemChange(_ref10) {
  var getState = _ref10.getState,
    setState = _ref10.setState,
    _ref10$params = _ref10.params,
    params = _ref10$params === void 0 ? EMPTY_OBJECT : _ref10$params;
  var fieldKey = params.fieldKey;
  if (fieldKey === 'type') {
    return handleTypeChangeInListItem({
      getState: getState,
      setState: setState,
      params: params
    });
  }
  return handleChangeInListItem({
    getState: getState,
    setState: setState,
    params: params
  });
};
var handleSaveAsNew = function handleSaveAsNew(_ref11) {
  var setState = _ref11.setState,
    getState = _ref11.getState;
  var _getState7 = getState(),
    values = _getState7.values,
    filterTypes = _getState7.filterTypes,
    defaultFilterTypes = _getState7.defaultFilterTypes,
    isReadOnly = _getState7.isReadOnly;
  setState({
    editMode: MODES.NEW,
    fields: getFields(defaultFilterTypes, values, filterTypes, isReadOnly)
  });
};
var handleRename = function handleRename(_ref12) {
  var setState = _ref12.setState;
  setState({
    editMode: MODES.EDIT,
    isRename: true
  });
};
var handleEditCancel = function handleEditCancel(_ref13) {
  var setState = _ref13.setState;
  setState({
    editMode: undefined,
    isRename: false
  });
};
var handleFilterGroupNameChange = function handleFilterGroupNameChange(_ref14) {
  var setState = _ref14.setState,
    _ref14$params = _ref14.params,
    params = _ref14$params === void 0 ? EMPTY_OBJECT : _ref14$params,
    getState = _ref14.getState;
  var filterGroupName = params.filterGroupName;
  var _getState8 = getState(),
    values = _getState8.values,
    filterGroups = _getState8.filterGroups;
  var errors = validateFilterGroup(filterGroupName, values, filterGroups, true);
  setState({
    filterGroupName: filterGroupName,
    errors: errors
  });
};
var saveNewFilterGroup = function saveNewFilterGroup(_ref15) {
  var getState = _ref15.getState,
    setState = _ref15.setState;
  var _getState9 = getState(),
    filterGroupName = _getState9.filterGroupName,
    values = _getState9.values,
    assetType = _getState9.assetType,
    filterGroups = _getState9.filterGroups,
    onFilterGroupModified = _getState9.onFilterGroupModified,
    filterTypes = _getState9.filterTypes,
    sections = _getState9.sections,
    module = _getState9.module,
    _getState9$createFilt = _getState9.createFilterGroup,
    createFilterGroup = _getState9$createFilt === void 0 ? defaultFilterService.createFilterGroup : _getState9$createFilt;
  var filters = getFilterFromFormValues(sections, values);
  var filterGroup = getFilterGroupPayload({
    groupName: filterGroupName,
    values: values,
    filterTypes: filterTypes
  });
  setState({
    editMode: undefined,
    isRename: false,
    filterGroupName: undefined,
    isModified: false,
    selectedFilters: EMPTY_ARRAY
  });
  if (assetType) {
    fireSaveNewFilterGroupClickStreamEvent({
      module: module
    });
    createFilterGroup(assetType, filterGroup).then(function (response) {
      var newFilterGroup = getDataFromResponse(response);
      var updatedFilterGroups = [].concat(_toConsumableArray(filterGroups), [getFilterGroup(newFilterGroup, filters)]);
      onFilterGroupModified(updatedFilterGroups);
      setState({
        selectedFilterGroup: newFilterGroup.id
      });
    });
  }
};
var getSelectedFilterGroupName = function getSelectedFilterGroupName(selectedFilterGroupId, filterGroups) {
  var selectedFilterGroup = _find(filterGroups, function (filterGroup) {
    return filterGroupReader.id(filterGroup) === selectedFilterGroupId;
  }) || EMPTY_OBJECT;
  return filterGroupReader.name(selectedFilterGroup);
};
var updateFilterGroup = function updateFilterGroup(_ref16) {
  var getState = _ref16.getState,
    setState = _ref16.setState;
  var _getState10 = getState(),
    filterGroups = _getState10.filterGroups,
    selectedFilterGroup = _getState10.selectedFilterGroup,
    values = _getState10.values,
    filterGroupName = _getState10.filterGroupName,
    assetType = _getState10.assetType,
    onFilterGroupModified = _getState10.onFilterGroupModified,
    filterTypes = _getState10.filterTypes,
    sections = _getState10.sections,
    module = _getState10.module,
    _getState10$updateFil = _getState10.updateFilterGroup,
    updateFilterGroupAction = _getState10$updateFil === void 0 ? defaultFilterService.updateFilterGroup : _getState10$updateFil;
  var filters = getFilterFromFormValues(sections, values);
  var groupName = filterGroupName || getSelectedFilterGroupName(selectedFilterGroup, filterGroups);
  var selectedFilterGroupInfo = _find(filterGroups, function (filterGroup) {
    return filterGroupReader.id(filterGroup) === selectedFilterGroup;
  }) || EMPTY_OBJECT;
  var updatedFilterGroup = getFilterGroupPayload({
    groupName: groupName,
    values: values,
    filterTypes: filterTypes,
    defaultGroupIdentifier: filterGroupReader.defaultGroupIdentifier(selectedFilterGroupInfo),
    nonRenamable: filterGroupReader.nonRenamable(selectedFilterGroupInfo),
    nonDeletable: filterGroupReader.nonDeletable(selectedFilterGroupInfo)
  });
  setState({
    editMode: undefined,
    isRename: false,
    filterGroupName: undefined,
    isModified: false
  });
  if (assetType) {
    fireUpdateFilterGroupClickStreamEvent({
      module: module
    });
    updateFilterGroupAction(assetType, selectedFilterGroup, updatedFilterGroup).then(function (response) {
      var savedFilterGroup = getDataFromResponse(response);
      var updatedFilterGroups = _map(filterGroups, function (filter) {
        return filter.id === savedFilterGroup.id ? getFilterGroup(savedFilterGroup, filters) : filter;
      });
      onFilterGroupModified(updatedFilterGroups);
      setState({
        selectedFilterGroup: savedFilterGroup.id
      });
    });
  }
};
var SAVE_FILTER_HANDLERS = (_SAVE_FILTER_HANDLERS = {}, _defineProperty(_SAVE_FILTER_HANDLERS, MODES.NEW, saveNewFilterGroup), _defineProperty(_SAVE_FILTER_HANDLERS, MODES.EDIT, updateFilterGroup), _SAVE_FILTER_HANDLERS);
var handleSaveFilterGroup = function handleSaveFilterGroup(_ref17) {
  var setState = _ref17.setState,
    getState = _ref17.getState;
  var _getState11 = getState(),
    editMode = _getState11.editMode,
    filterGroupName = _getState11.filterGroupName,
    values = _getState11.values,
    filterGroups = _getState11.filterGroups,
    isRename = _getState11.isRename;
  var validateFilterGroupName = editMode === MODES.NEW || editMode === MODES.EDIT && isRename;
  var errors = validateFilterGroup(filterGroupName, values, filterGroups, validateFilterGroupName);
  if (_isEmpty(errors)) {
    SAVE_FILTER_HANDLERS[editMode]({
      setState: setState,
      getState: getState
    });
  }
  setState({
    errors: errors
  });
};
var handleDeleteFilterGroup = function handleDeleteFilterGroup(_ref18) {
  var getState = _ref18.getState,
    setState = _ref18.setState;
  var _getState12 = getState(),
    selectedFilterGroup = _getState12.selectedFilterGroup,
    assetType = _getState12.assetType,
    filterGroups = _getState12.filterGroups,
    onFilterGroupModified = _getState12.onFilterGroupModified,
    _getState12$deletedFi = _getState12.deletedFiltersList,
    deletedFiltersList = _getState12$deletedFi === void 0 ? EMPTY_ARRAY : _getState12$deletedFi,
    module = _getState12.module,
    _getState12$deleteFil = _getState12.deleteFilterGroup,
    deleteFilterGroup = _getState12$deleteFil === void 0 ? defaultFilterService.deleteFilterGroup : _getState12$deleteFil;
  deleteFilterGroup(assetType, selectedFilterGroup);
  fireDeleteFilterGroupClickStreamEvent({
    module: module
  });
  var updatedFilterGroups = _filter(filterGroups, function (group) {
    return filterGroupReader.id(group) !== selectedFilterGroup;
  });
  onFilterGroupModified(updatedFilterGroups);
  setState({
    selectedFilterGroup: DEFAULT_FILTER_GROUP,
    editMode: undefined,
    isRename: false,
    filterGroupName: undefined,
    isModified: false,
    deletedFiltersList: [].concat(_toConsumableArray(deletedFiltersList), [selectedFilterGroup])
  }, function () {
    handleSelectedFilterGroupChange({
      getState: getState,
      setState: setState,
      params: {
        selectedFilterGroup: DEFAULT_FILTER_GROUP
      }
    });
  });
};
var handleCloseFilterDialog = function handleCloseFilterDialog(_ref19) {
  var setState = _ref19.setState,
    getState = _ref19.getState;
  var _getState13 = getState(),
    _getState13$deletedFi = _getState13.deletedFiltersList,
    deletedFiltersList = _getState13$deletedFi === void 0 ? EMPTY_ARRAY : _getState13$deletedFi,
    onFilterApply = _getState13.onFilterApply,
    appliedFilterGroup = _getState13.appliedFilterGroup;
  if (deletedFiltersList.includes(appliedFilterGroup)) {
    onFilterApply(EMPTY_ARRAY);
  }
  setState({
    isPopoverVisible: false
  });
};
var deleteFromRows = function deleteFromRows(sections, rowId) {
  var section = _head(sections) || EMPTY_OBJECT;
  var rows = section.rows;
  var rowsWithoutRowId = _without(rows, rowId);
  return [_objectSpread(_objectSpread({}, section), {}, {
    rows: rowsWithoutRowId
  })];
};

// YET TO IMPLEMENT REMAINING OPTION FIELD
var isSelected = function isSelected(selectedValues, filterTypeId) {
  var filterExists = _find(selectedValues, {
    type: filterTypeId
  });
  return Boolean(filterExists);
};
var getRemainingFilterTypes = function getRemainingFilterTypes(selectedValues, filterTypes) {
  var remainingFilterTypes = _reduce(filterTypes, function (acc, filterType) {
    var selected = isSelected(selectedValues, filterType);
    return selected ? acc : [].concat(_toConsumableArray(acc), [filterType]);
  }, []);
  return remainingFilterTypes;
};

// eslint-disable-next-line no-unused-vars
var getRemainingOptionFields = function getRemainingOptionFields(fields, selectedValues, filterTypes) {
  var filterTypeId = getFilterId(filterTypes);
  var remainingFilterTypes = getRemainingFilterTypes(_values(selectedValues), filterTypeId);
  var selectedFilterType = _get(selectedValues, filterTypeId, '');
  var updatedFields = _map(fields, function (field) {
    return _objectSpread(_objectSpread({}, field), {}, {
      renderOptions: _objectSpread(_objectSpread({}, field.renderOptions), {}, {
        filterTypes: [].concat(_toConsumableArray(remainingFilterTypes), [selectedFilterType])
      })
    });
  });
  return _keyBy(updatedFields, 'id');
};
var handleRemoveFilterRow = function handleRemoveFilterRow(_ref20) {
  var setState = _ref20.setState,
    _ref20$params = _ref20.params,
    params = _ref20$params === void 0 ? EMPTY_OBJECT : _ref20$params,
    getState = _ref20.getState;
  var _getState14 = getState(),
    currentValues = _getState14.values,
    currentSections = _getState14.sections,
    selectedFilterGroup = _getState14.selectedFilterGroup,
    filterTypes = _getState14.filterTypes,
    filterGroups = _getState14.filterGroups,
    defaultFilterTypes = _getState14.defaultFilterTypes,
    isReadOnly = _getState14.isReadOnly;
  var rowId = params.id;
  if (_size(currentValues) === 1) {
    setState({
      values: _defineProperty({}, rowId, _objectSpread(_objectSpread({}, currentValues[rowId]), {}, {
        values: []
      }))
    });
  } else {
    var removedValues = _omit(currentValues, [rowId]);
    var fields = getFields(defaultFilterTypes, removedValues, filterTypes, filterGroups, selectedFilterGroup, isReadOnly);
    var removedRows = deleteFromRows(currentSections, rowId);
    var isModified = isFilterGroupEdited({
      getState: getState,
      sections: removedRows,
      values: removedValues,
      selectedFilterGroup: selectedFilterGroup
    });
    setState({
      values: removedValues,
      fields: fields,
      sections: removedRows,
      isModified: isModified,
      showAddFilter: canAddNewFilterRows(_get(removedRows, '0.rows'), filterTypes)
    });
  }
};
var handleResetFilters = function handleResetFilters(_ref21) {
  var getState = _ref21.getState,
    setState = _ref21.setState;
  var _getState15 = getState(),
    filterGroups = _getState15.filterGroups,
    selectedFilterGroup = _getState15.selectedFilterGroup,
    filterTypes = _getState15.filterTypes,
    defaultFilterTypes = _getState15.defaultFilterTypes,
    isReadOnly = _getState15.isReadOnly;
  var initialValuesMap = getInitialValues(filterGroups, selectedFilterGroup);
  var initialFields = getFields(defaultFilterTypes, initialValuesMap, filterTypes, filterGroups, selectedFilterGroup, isReadOnly);
  var appliedFilterGroup = getSelectedFilterGroup(selectedFilterGroup, filterGroups);
  var filters = filterGroupReader.filters(appliedFilterGroup);
  var initialSectionRows = getSectionRows(initialValuesMap, filters);
  var initialSections = createFormSections(initialSectionRows);
  var isAllFilterTypesAlreadyAdded = _size(initialValuesMap) === _size(filterTypes);
  setState({
    fields: initialFields,
    values: initialValuesMap,
    sections: initialSections,
    isModified: false,
    showAddFilter: !isAllFilterTypesAlreadyAdded
  });
};
var getPreviouslyAppliedFilterGroupId = function getPreviouslyAppliedFilterGroupId() {
  var deletedFiltersList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var appliedFilterGroup = arguments.length > 1 ? arguments[1] : undefined;
  var filterGroups = arguments.length > 2 ? arguments[2] : undefined;
  var previouslyAppliedFilterGroup = !deletedFiltersList.includes(appliedFilterGroup) && _find(filterGroups, function (group) {
    return filterGroupReader.id(group) === appliedFilterGroup;
  });
  return previouslyAppliedFilterGroup || EMPTY_OBJECT;
};
var handlePersistDefaultFilterUpdate = function handlePersistDefaultFilterUpdate(_ref22) {
  var groupName = _ref22.groupName,
    values = _ref22.values,
    filterGroups = _ref22.filterGroups,
    assetType = _ref22.assetType,
    onFilterGroupModified = _ref22.onFilterGroupModified,
    filterTypes = _ref22.filterTypes,
    sections = _ref22.sections,
    saveDefaultFilters = _ref22.saveDefaultFilters;
  var filters = _get(getFilterGroupPayload({
    groupName: groupName,
    values: values,
    filterTypes: filterTypes
  }), 'filters');
  var filtersForGroup = getFilterFromFormValues(sections, values);
  var updatedFilterGroups = _map(filterGroups, function (filter) {
    return filter.id === DEFAULT_FILTER_GROUP ? _objectSpread(_objectSpread({}, filter), {}, {
      filters: filtersForGroup
    }) : filter;
  });
  onFilterGroupModified(updatedFilterGroups);
  saveDefaultFilters(assetType, {
    groupId: null,
    filters: filters
  });
};
var DEFAULT_FILTER_BEHAVIOR_VS_FILTER_UPDATE = _defineProperty({}, DEFAULT_FILTER_BEHAVIOR.PERSIST, handlePersistDefaultFilterUpdate);
var handleAppliedFilterUpdate = function handleAppliedFilterUpdate(_ref23) {
  var assetType = _ref23.assetType,
    selectedFilterGroup = _ref23.selectedFilterGroup,
    appliedFilterGroup = _ref23.appliedFilterGroup,
    filterGroups = _ref23.filterGroups,
    values = _ref23.values,
    deletedFiltersList = _ref23.deletedFiltersList,
    defaultFilterBehavior = _ref23.defaultFilterBehavior,
    onFilterGroupModified = _ref23.onFilterGroupModified,
    filterTypes = _ref23.filterTypes,
    sections = _ref23.sections,
    saveDefaultFilters = _ref23.saveDefaultFilters,
    updateFilterGroupAction = _ref23.updateFilterGroup;
  var groupName = getSelectedFilterGroupName(selectedFilterGroup, filterGroups);
  if (assetType) {
    if (selectedFilterGroup === DEFAULT_FILTER_GROUP) {
      var funcToExec = DEFAULT_FILTER_BEHAVIOR_VS_FILTER_UPDATE[defaultFilterBehavior] || _noop;
      funcToExec({
        groupName: groupName,
        values: values,
        filterGroups: filterGroups,
        assetType: assetType,
        onFilterGroupModified: onFilterGroupModified,
        filterTypes: filterTypes,
        sections: sections,
        saveDefaultFilters: saveDefaultFilters
      });
    }
    var previouslyAppliedFilterGroup = getPreviouslyAppliedFilterGroupId(deletedFiltersList, appliedFilterGroup, filterGroups);
    var filterGroupInfo = _find(filterGroups, function (filterGroup) {
      return filterGroupReader.id(filterGroup) === selectedFilterGroup;
    }) || EMPTY_OBJECT;
    if (!_isEmpty(previouslyAppliedFilterGroup) && previouslyAppliedFilterGroup.id !== DEFAULT_FILTER_GROUP && selectedFilterGroup === DEFAULT_FILTER_GROUP && previouslyAppliedFilterGroup) {
      var filterGroup = getFilterGroupPayload({
        groupName: previouslyAppliedFilterGroup.name,
        values: previouslyAppliedFilterGroup.filters,
        isAppliedFilter: false,
        filterTypes: filterTypes,
        defaultGroupIdentifier: filterGroupReader.defaultGroupIdentifier(previouslyAppliedFilterGroup),
        nonRenamable: filterGroupReader.nonRenamable(previouslyAppliedFilterGroup),
        nonDeletable: filterGroupReader.nonDeletable(previouslyAppliedFilterGroup)
      });
      updateFilterGroupAction(assetType, previouslyAppliedFilterGroup.id, filterGroup);
    }
    if (selectedFilterGroup !== DEFAULT_FILTER_GROUP) {
      var updatedFilterGroup = getFilterGroupPayload({
        groupName: groupName,
        values: values,
        isAppliedFilter: true,
        filterTypes: filterTypes,
        defaultGroupIdentifier: filterGroupReader.defaultGroupIdentifier(filterGroupInfo),
        nonRenamable: filterGroupReader.nonRenamable(filterGroupInfo),
        nonDeletable: filterGroupReader.nonDeletable(filterGroupInfo)
      });
      updateFilterGroupAction(assetType, selectedFilterGroup, updatedFilterGroup);
    }
  }
};
var handleApplyFilters = function handleApplyFilters(_ref24) {
  var getState = _ref24.getState,
    setState = _ref24.setState;
  var _getState16 = getState(),
    onFilterApply = _getState16.onFilterApply,
    values = _getState16.values,
    sections = _getState16.sections,
    selectedFilterGroup = _getState16.selectedFilterGroup,
    assetType = _getState16.assetType,
    filterGroups = _getState16.filterGroups,
    appliedFilterGroup = _getState16.appliedFilterGroup,
    onFilterGroupModified = _getState16.onFilterGroupModified,
    deletedFiltersList = _getState16.deletedFiltersList,
    defaultFilterBehavior = _getState16.defaultFilterBehavior,
    filterTypes = _getState16.filterTypes,
    module = _getState16.module,
    _getState16$saveDefau = _getState16.saveDefaultFilters,
    saveDefaultFilters = _getState16$saveDefau === void 0 ? defaultFilterService.saveDefaultFilters : _getState16$saveDefau,
    _getState16$updateFil = _getState16.updateFilterGroup,
    updateFilterGroupAction = _getState16$updateFil === void 0 ? defaultFilterService.updateFilterGroup : _getState16$updateFil;
  fireApplyFilterGroupClickStreamEvent({
    module: module
  });
  var filters = getFilterFromFormValues(sections, values);
  handleAppliedFilterUpdate({
    assetType: assetType,
    selectedFilterGroup: selectedFilterGroup,
    appliedFilterGroup: appliedFilterGroup,
    filterGroups: filterGroups,
    values: values,
    deletedFiltersList: deletedFiltersList,
    defaultFilterBehavior: defaultFilterBehavior,
    onFilterGroupModified: onFilterGroupModified,
    filterTypes: filterTypes,
    sections: sections,
    saveDefaultFilters: saveDefaultFilters,
    updateFilterGroup: updateFilterGroupAction
  });
  onFilterApply(filters, selectedFilterGroup);
  handlePopoverHide({
    setState: setState
  });
};
var refreshFieldsOnFilterTypesChange = function refreshFieldsOnFilterTypesChange(_ref25) {
  var setState = _ref25.setState,
    getState = _ref25.getState;
  var _getState17 = getState(),
    currentValues = _getState17.values,
    selectedFilterGroup = _getState17.selectedFilterGroup,
    filterTypes = _getState17.filterTypes,
    filterGroups = _getState17.filterGroups,
    defaultFilterTypes = _getState17.defaultFilterTypes,
    isReadOnly = _getState17.isReadOnly;
  var fields = getFields(defaultFilterTypes, currentValues, filterTypes, filterGroups, selectedFilterGroup, isReadOnly);
  setState({
    fields: fields
  });
};
var ACTION_HANDLERS = (_ACTION_HANDLERS = {}, _defineProperty(_ACTION_HANDLERS, ADD_NEW_FILTER, handleAddNewFilter), _defineProperty(_ACTION_HANDLERS, HIDE_FILTER_POPOVER, handlePopoverHide), _defineProperty(_ACTION_HANDLERS, SHOW_FILTER_POPOVER, handlePopoverShow), _defineProperty(_ACTION_HANDLERS, FORM_ACTION_TYPES.ON_FIELD_CHANGE, handleListItemChange), _defineProperty(_ACTION_HANDLERS, FILTER_GROUP_CHANGE, handleSelectedFilterGroupChange), _defineProperty(_ACTION_HANDLERS, SAVE_AS_NEW_FILTER_GROUP, handleSaveAsNew), _defineProperty(_ACTION_HANDLERS, FILTER_GROUP_EDIT_CANCEL, handleEditCancel), _defineProperty(_ACTION_HANDLERS, FILTER_GROUP_NAME_CHANGE, handleFilterGroupNameChange), _defineProperty(_ACTION_HANDLERS, FILTER_GROUP_SAVE, handleSaveFilterGroup), _defineProperty(_ACTION_HANDLERS, FILTER_GROUP_UPDATE, updateFilterGroup), _defineProperty(_ACTION_HANDLERS, RENAME_FILTER_GROUP, handleRename), _defineProperty(_ACTION_HANDLERS, DELETE_FILTER_GROUP, handleDeleteFilterGroup), _defineProperty(_ACTION_HANDLERS, CLOSE_FILTER_DIALOG, handleCloseFilterDialog), _defineProperty(_ACTION_HANDLERS, "DELETE_FILTER_LIST_ITEM", handleRemoveFilterRow), _defineProperty(_ACTION_HANDLERS, RESET_FILTER, handleResetFilters), _defineProperty(_ACTION_HANDLERS, APPLY_FILTERS, handleApplyFilters), _defineProperty(_ACTION_HANDLERS, REFRESH_FILTER_TYPES, refreshFieldsOnFilterTypesChange), _ACTION_HANDLERS);
export default ACTION_HANDLERS;